import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import toast from "react-hot-toast";

import { DialogNotice } from "@/components/common/ActionDialog";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { Field, FieldRow } from "@/components/settings/settings-kit";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  fetchPerformanceCampaigns,
  fetchPerformanceEvents,
  fetchPerformanceRules,
  removePerformanceRule,
  resumePerformanceAd,
  upsertPerformanceRule,
  type RuleAction,
  type RuleEventView,
  type RuleView,
} from "@/services/metaPerformance.service";
import { formatDate } from "@/utils/formatters";

import { errorMessage } from "../../tiktok/tiktokFormat";
import type { PerfScope } from "./AdPerformancePage";
import { EventOutcome } from "./bits";
import { METRIC_LABEL, fmtMetricValue } from "./format";

interface Draft {
  roasFloor: string;
  roasCeiling: string;
  ctrFloor: string;
  ctrCeiling: string;
  windowDays: string;
  minSpend: string;
  action: RuleAction;
  enabled: boolean;
}
const EMPTY: Draft = { roasFloor: "", roasCeiling: "", ctrFloor: "", ctrCeiling: "", windowDays: "3", minSpend: "20", action: "NOTIFY", enabled: true };

function fromRule(r: RuleView | undefined): Draft {
  if (!r) return EMPTY;
  return {
    roasFloor: r.roasFloor?.toString() ?? "",
    roasCeiling: r.roasCeiling?.toString() ?? "",
    ctrFloor: r.ctrFloor?.toString() ?? "",
    ctrCeiling: r.ctrCeiling?.toString() ?? "",
    windowDays: String(r.windowDays),
    minSpend: String(r.minSpend),
    action: r.action,
    enabled: r.enabled,
  };
}

const opt = (s: string): number | null => (s.trim() === "" ? null : Number.parseFloat(s));

/**
 * Per-campaign guard rails: ROAS and CTR floors and ceilings, checked every
 * few hours over a short window. A floor can pause the ad; a ceiling only
 * flags it as a scale candidate. Every breach lands in the log below.
 */
export function RulesTab({ scope }: { scope: PerfScope }) {
  const queryClient = useQueryClient();
  const campaigns = useQuery({
    queryKey: ["meta-performance", "campaigns", scope.days, false],
    queryFn: () => fetchPerformanceCampaigns(scope.days, false),
  });
  const rules = useQuery({ queryKey: ["meta-performance", "rules"], queryFn: fetchPerformanceRules });
  const [campaignId, setCampaignId] = useState("");
  const [draft, setDraft] = useState<Draft>(EMPTY);

  const currency = campaigns.data?.currency.account ?? "";
  const campaignList = useMemo(() => campaigns.data?.campaigns ?? [], [campaigns.data]);
  const campaign = campaignList.find((c) => c.id === campaignId) ?? null;
  const existing = rules.data?.find((r) => r.metaCampaignId === campaignId);

  useEffect(() => {
    if (!campaignId && campaignList.length) setCampaignId(campaignList[0].id);
  }, [campaignId, campaignList]);
  useEffect(() => {
    setDraft(fromRule(existing));
  }, [existing, campaignId]);

  const save = useMutation({
    mutationFn: () => {
      const floor = opt(draft.roasFloor);
      const ceiling = opt(draft.roasCeiling);
      const cf = opt(draft.ctrFloor);
      const cc = opt(draft.ctrCeiling);
      if ([floor, ceiling, cf, cc].every((v) => v === null)) throw new Error("Set at least one threshold");
      if (floor !== null && ceiling !== null && floor >= ceiling) throw new Error("ROAS floor must be below the ceiling");
      if (cf !== null && cc !== null && cf >= cc) throw new Error("CTR floor must be below the ceiling");
      return upsertPerformanceRule(campaignId, {
        roasFloor: floor,
        roasCeiling: ceiling,
        ctrFloor: cf,
        ctrCeiling: cc,
        windowDays: Math.min(30, Math.max(1, Number.parseInt(draft.windowDays, 10) || 3)),
        minSpend: Math.max(0, Number.parseFloat(draft.minSpend) || 0),
        action: draft.action,
        enabled: draft.enabled,
      });
    },
    onSuccess: () => {
      toast.success("Rule saved");
      void queryClient.invalidateQueries({ queryKey: ["meta-performance"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => removePerformanceRule(campaignId),
    onSuccess: () => {
      toast.success("Rule removed");
      void queryClient.invalidateQueries({ queryKey: ["meta-performance"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const preview = useMemo(() => sentence(draft, campaign?.name ?? "this campaign", currency), [draft, campaign?.name, currency]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(fromRule(existing));

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <DashboardCard title="Rule for a campaign" bodyClassName="space-y-4 px-5 pb-5">
          <Field label="Campaign" htmlFor="rule-campaign" help="One rule per campaign; it applies to every ad inside">
            {campaigns.isLoading ? (
              <div className="h-9 w-full rounded-lg bg-muted" />
            ) : campaignList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No campaigns on this ad account yet.</p>
            ) : (
              <Select value={campaignId} onValueChange={(v) => v && setCampaignId(v)}>
                <SelectTrigger id="rule-campaign"><SelectValue placeholder="Choose a campaign" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {campaignList.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{rules.data?.some((r) => r.metaCampaignId === c.id) ? " · has rule" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <FieldRow>
            <Field label="ROAS floor" htmlFor="rule-roas-floor" help="Below this an ad is flagged, or paused">
              <Num id="rule-roas-floor" value={draft.roasFloor} onChange={(v) => setDraft({ ...draft, roasFloor: v })} suffix="x" step={0.1} placeholder="1.0" />
            </Field>
            <Field label="ROAS ceiling" htmlFor="rule-roas-ceiling" help="At or above this an ad is a scale candidate">
              <Num id="rule-roas-ceiling" value={draft.roasCeiling} onChange={(v) => setDraft({ ...draft, roasCeiling: v })} suffix="x" step={0.1} placeholder="1.9" />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="CTR floor" htmlFor="rule-ctr-floor">
              <Num id="rule-ctr-floor" value={draft.ctrFloor} onChange={(v) => setDraft({ ...draft, ctrFloor: v })} suffix="%" step={0.1} placeholder="0.8" />
            </Field>
            <Field label="CTR ceiling" htmlFor="rule-ctr-ceiling">
              <Num id="rule-ctr-ceiling" value={draft.ctrCeiling} onChange={(v) => setDraft({ ...draft, ctrCeiling: v })} suffix="%" step={0.1} placeholder="3" />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Look back" htmlFor="rule-window" help="Days of data each check uses">
              <Num id="rule-window" value={draft.windowDays} onChange={(v) => setDraft({ ...draft, windowDays: v })} suffix="days" step={1} />
            </Field>
            <Field label="Minimum spend" htmlFor="rule-min-spend" help="An ad is only judged after spending this much in the window">
              <Num id="rule-min-spend" value={draft.minSpend} onChange={(v) => setDraft({ ...draft, minSpend: v })} prefix={currency} step={1} />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="When a floor is crossed" htmlFor="rule-action">
              <Select value={draft.action} onValueChange={(v) => v && setDraft({ ...draft, action: v as RuleAction })}>
                <SelectTrigger id="rule-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NOTIFY">Notify me</SelectItem>
                  <SelectItem value="PAUSE_AND_NOTIFY">Pause the ad on Meta and notify me</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Enabled" htmlFor="rule-enabled">
              <label htmlFor="rule-enabled" className="flex h-9 cursor-pointer items-center gap-2 text-sm text-foreground">
                <Switch id="rule-enabled" checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} />
                {draft.enabled ? "Checked every six hours" : "Paused, nothing is checked"}
              </label>
            </Field>
          </FieldRow>

          <DialogNotice tone={draft.action === "PAUSE_AND_NOTIFY" ? "warning" : "success"}>{preview}</DialogNotice>

          <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
            {existing && (
              <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className="mr-auto text-xs font-medium text-destructive underline-offset-4 hover:underline">
                Remove rule
              </button>
            )}
            <Button type="button" onClick={() => save.mutate()} disabled={!campaignId || save.isPending || (!!existing && !dirty)}>
              {save.isPending ? "Saving" : existing ? "Save changes" : "Create rule"}
            </Button>
          </div>
        </DashboardCard>

        <DashboardCard title="Your rules" bodyClassName="px-0 pb-0">
          <RulesList rules={rules.data ?? []} loading={rules.isLoading} names={new Map(campaignList.map((c) => [c.id, c.name]))} onPick={setCampaignId} picked={campaignId} />
        </DashboardCard>
      </div>

      <EventsLog scope={scope} />
    </div>
  );
}

function sentence(d: Draft, name: string, currency: string): string {
  const parts: string[] = [];
  if (d.roasFloor.trim()) parts.push(`ROAS falls below ${d.roasFloor}x`);
  if (d.ctrFloor.trim()) parts.push(`CTR falls below ${d.ctrFloor}%`);
  const ups: string[] = [];
  if (d.roasCeiling.trim()) ups.push(`ROAS reaches ${d.roasCeiling}x`);
  if (d.ctrCeiling.trim()) ups.push(`CTR reaches ${d.ctrCeiling}%`);
  const win = `over the last ${d.windowDays || "3"} day${d.windowDays === "1" ? "" : "s"} after ${currency ? `${currency} ` : ""}${d.minSpend || "0"} spent`;
  const out: string[] = [];
  if (parts.length) out.push(`${d.action === "PAUSE_AND_NOTIFY" ? "Pause" : "Flag"} any ad in "${name}" whose ${parts.join(" or ")} ${win}${d.action === "PAUSE_AND_NOTIFY" ? ", and email me" : " and email me"}.`);
  if (ups.length) out.push(`Tell me when an ad's ${ups.join(" or ")} ${win}, as a scale candidate.`);
  return out.join(" ") || "Set a floor or a ceiling to describe the rule.";
}

function Num({ id, value, onChange, prefix, suffix, step, placeholder }: { id: string; value: string; onChange: (v: string) => void; prefix?: string; suffix?: string; step?: number; placeholder?: string }) {
  return (
    <div className="relative">
      {prefix && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs text-muted-foreground">{prefix}</span>}
      <Input id={id} type="number" inputMode="decimal" min={0} step={step} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${prefix ? "pl-12 " : ""}${suffix ? "pr-12 " : ""}tabular-nums`} />
      {suffix && <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">{suffix}</span>}
    </div>
  );
}

function RulesList({ rules, loading, names, onPick, picked }: { rules: RuleView[]; loading: boolean; names: Map<string, string>; onPick: (id: string) => void; picked: string }) {
  if (loading) return <div className="m-5 h-24 animate-pulse rounded-md bg-muted" />;
  if (rules.length === 0) return <p className="px-5 pb-5 text-sm text-muted-foreground">No rules yet. Pick a campaign on the left and set a floor or ceiling.</p>;
  return (
    <ul className="divide-y divide-border">
      {rules.map((r) => (
        <li key={r.id}>
          <button type="button" onClick={() => onPick(r.metaCampaignId)} className={`flex w-full items-start justify-between gap-3 px-5 py-3 text-left hover:bg-muted/40 ${picked === r.metaCampaignId ? "bg-muted/40" : ""}`}>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-foreground">{names.get(r.metaCampaignId) ?? r.metaCampaignId}</div>
              <div className="truncate text-xs text-muted-foreground">
                {[r.roasFloor != null ? `ROAS < ${r.roasFloor}x` : null, r.roasCeiling != null ? `ROAS ≥ ${r.roasCeiling}x` : null, r.ctrFloor != null ? `CTR < ${r.ctrFloor}%` : null, r.ctrCeiling != null ? `CTR ≥ ${r.ctrCeiling}%` : null].filter(Boolean).join(" · ")}
                {` · ${r.action === "PAUSE_AND_NOTIFY" ? "pauses" : "notifies"}`}
                {r.enabled ? "" : " · off"}
              </div>
            </div>
            {r.openEvents > 0 && <span className="shrink-0 text-xs font-medium text-amber-700 dark:text-amber-400">{r.openEvents} open</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

function EventsLog({ scope }: { scope: PerfScope }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [openOnly, setOpenOnly] = useState(false);
  const q = useQuery({
    queryKey: ["meta-performance", "events", page, openOnly],
    queryFn: () => fetchPerformanceEvents({ page, limit: 20, open: openOnly }),
    placeholderData: (prev) => prev,
  });
  const resume = useMutation({
    mutationFn: (adId: string) => resumePerformanceAd(adId),
    onSuccess: () => {
      toast.success("Ad is live again on Meta");
      void queryClient.invalidateQueries({ queryKey: ["meta-performance"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const columns = useMemo<ColumnDef<RuleEventView>[]>(
    () => [
      { id: "when", header: "When", meta: { fit: true }, cell: ({ row }) => <span className="whitespace-nowrap text-muted-foreground">{formatDate(row.original.createdAt)}</span> },
      {
        id: "ad",
        header: "Ad",
        cell: ({ row }) => (
          <button type="button" onClick={() => scope.openAd(row.original.metaAdId)} className="truncate font-medium text-foreground underline-offset-4 hover:underline">
            {row.original.adName ?? row.original.metaAdId}
          </button>
        ),
      },
      { id: "what", header: "What happened", cell: ({ row }) => <span>{METRIC_LABEL[row.original.metric]}: <span className="tabular-nums">{fmtMetricValue(row.original.metric, row.original.observed)}</span> vs <span className="tabular-nums">{fmtMetricValue(row.original.metric, row.original.threshold)}</span></span> },
      { id: "outcome", header: "Outcome", meta: { fit: true }, cell: ({ row }) => <EventOutcome outcome={row.original.outcome} /> },
      {
        id: "state",
        header: "",
        meta: { fit: true, cellClassName: "text-right" },
        cell: ({ row }) => {
          const e = row.original;
          if (e.resolvedAt) return <span className="text-xs text-muted-foreground">Cleared {formatDate(e.resolvedAt)}</span>;
          if (e.outcome === "PAUSED") {
            return (
              <Button size="sm" variant="outline" onClick={() => resume.mutate(e.metaAdId)} disabled={resume.isPending}>Resume</Button>
            );
          }
          return <span className="text-xs text-amber-700 dark:text-amber-400">Open</span>;
        },
      },
    ],
    [scope, resume],
  );
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 20));
  return (
    <DashboardCard
      title="Rule log"
      bodyClassName="px-0 pb-0"
      action={
        <label htmlFor="events-open" className="flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground">
          <Switch id="events-open" checked={openOnly} onCheckedChange={(v) => { setOpenOnly(v); setPage(1); }} />
          Open only
        </label>
      }
    >
      <DataTable columns={columns} data={q.data?.events ?? []} isLoading={q.isLoading} getRowId={(r) => r.id} emptyText="Nothing has crossed a rule yet" />
      {pages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-muted-foreground">
          <span>{total} event{total === 1 ? "" : "s"}</span>
          <span className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <span className="tabular-nums">{page} / {pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </span>
        </div>
      )}
    </DashboardCard>
  );
}
